import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Paper,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Cancel, Info } from "@mui/icons-material";

const InstructionsModal = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: "12px", sm: "16px" },
          padding: { xs: "12px", sm: "20px" },
          margin: { xs: "8px", sm: "32px" },
          direction: "rtl",
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          padding: { xs: "12px 0", sm: "20px 0" },
          position: "relative",
        }}
      >
        {/* Cancel button - Top right corner */}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: { xs: "8px", sm: "12px" },
            right: { xs: "8px", sm: "12px" },
            color: "#6B7280",
            "&:focus": {
              backgroundColor: "#F3F4F6",
              color: "red",
            },
            "&:hover": {
              backgroundColor: "#F3F4F6",
              color: "red",
            },
          }}
        >
          <Cancel />
        </IconButton>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Info
            sx={{ color: "#1E293B", fontSize: { xs: "28px", sm: "32px" } }}
          />
          <Typography
            component="div"
            variant={isMobile ? "h5" : "h4"}
            sx={{
              fontWeight: "bold",
              color: "#1E293B",
              fontFamily: '"Cairo", "Noto Sans Arabic", sans-serif',
              fontSize: { xs: "1.5rem", sm: "2.125rem" },
            }}
          >
            التعليمات
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: { xs: "12px 0", sm: "20px 0" },
          direction: "rtl",
        }}
      >
        <Paper
          sx={{
            padding: { xs: "20px", sm: "30px" },
            backgroundColor: "#F8FAFC",
            borderRadius: { xs: "8px", sm: "12px" },
            direction: "rtl",
            textAlign: "right",
          }}
        >
          {/* Number of Players */}
          <Box sx={{ marginBottom: { xs: "20px", sm: "24px" } }}>
            <Typography
              variant="h6"
              sx={{
                color: "#1E293B",
                marginBottom: { xs: "8px", sm: "12px" },
                fontWeight: "bold",
                fontFamily: '"Cairo", "Noto Sans Arabic", sans-serif',
                fontSize: { xs: "1.1rem", sm: "1.25rem" },
              }}
            >
              👥 عدد اللاعبين:
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#374151",
                lineHeight: 1.8,
                fontFamily: '"Cairo", "Noto Sans Arabic", sans-serif',
                fontSize: { xs: "1rem", sm: "1.1rem" },
                textAlign: "right",
                direction: "rtl",
                paddingRight: { xs: "20px", sm: "28px" },
              }}
            >
              اللعبة مكوّنة من فريقين، ولكل فريق لون (أخضر - بنفسجي)
            </Typography>
          </Box>

          {/* How to Play */}
          <Box sx={{ marginBottom: { xs: "20px", sm: "24px" } }}>
            <Typography
              variant="h6"
              sx={{
                color: "#1E293B",
                marginBottom: { xs: "8px", sm: "12px" },
                fontWeight: "bold",
                fontFamily: '"Cairo", "Noto Sans Arabic", sans-serif',
                fontSize: { xs: "1.1rem", sm: "1.25rem" },
              }}
            >
              ▶️ طريقة اللعب:
            </Typography>
            <Box
              sx={{
                margin: 0,
                paddingRight: { xs: "20px", sm: "28px" },
                direction: "rtl",
                textAlign: "right",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: "#374151",
                  lineHeight: 1.8,
                  fontFamily: '"Cairo", "Noto Sans Arabic", sans-serif',
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  textAlign: "right",
                  marginBottom: { xs: "10px", sm: "12px" },
                }}
              >
                - يلعب الفريقان بالتناوب
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#374151",
                  lineHeight: 1.8,
                  fontFamily: '"Cairo", "Noto Sans Arabic", sans-serif',
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  textAlign: "right",
                  marginBottom: { xs: "10px", sm: "12px" },
                }}
              >
                - في كل دور، يقوم فريق باختيار حرف
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#374151",
                  lineHeight: 1.8,
                  fontFamily: '"Cairo", "Noto Sans Arabic", sans-serif',
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  textAlign: "right",
                  marginBottom: { xs: "10px", sm: "12px" },
                }}
              >
                - يجيب الفريق على السؤال بكلمة تبدأ بالحرف المُختار
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#374151",
                  lineHeight: 1.8,
                  fontFamily: '"Cairo", "Noto Sans Arabic", sans-serif',
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  textAlign: "right",
                  marginBottom: { xs: "10px", sm: "12px" },
                }}
              >
                - في حال انتهاء الوقت دون إجابة صحيحة، تنتقل محاولة الإجابة
                للفريق الآخر
              </Typography>
            </Box>
          </Box>

          {/* Game Goal */}
          <Box>
            <Typography
              variant="h6"
              sx={{
                color: "#1E293B",
                marginBottom: { xs: "8px", sm: "12px" },
                fontWeight: "bold",
                fontFamily: '"Cairo", "Noto Sans Arabic", sans-serif',
                fontSize: { xs: "1.1rem", sm: "1.25rem" },
              }}
            >
              🧩 هدف اللعبة:
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#374151",
                lineHeight: 1.8,
                fontFamily: '"Cairo", "Noto Sans Arabic", sans-serif',
                fontSize: { xs: "1rem", sm: "1.1rem" },
                textAlign: "right",
                direction: "rtl",
                paddingRight: { xs: "20px", sm: "28px" },
              }}
            >
              تكوين مسار متصل بين ضفتي لون الفريق
            </Typography>
          </Box>
        </Paper>

        {/* Close Button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginTop: { xs: "20px", sm: "24px" },
          }}
        >
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              backgroundColor: "#1E293B",
              color: "white",
              padding: { xs: "10px 24px", sm: "12px 32px" },
              fontSize: { xs: "0.9rem", sm: "1rem" },
              fontWeight: "bold",
              borderRadius: "8px",
              textTransform: "none",
              fontFamily: '"Cairo", "Noto Sans Arabic", sans-serif',
              "&:hover": {
                backgroundColor: "#4F46E5",
              },
            }}
          >
            فهمت
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default InstructionsModal;
